import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// VAPID public key - this should match your backend VAPID_PUBLIC_KEY
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || 'BEl62iUYgUivxIkv69yViEuiBIa40HIe8VgUvQJ4Kz8';

// Convert VAPID key to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const usePushNotifications = (providedAuth = null) => {
  // Use provided auth if available, otherwise fallback to useAuth hook
  // AuthContext exposes `currentUser`; providedAuth from PushNotificationContext passes it as `user`.
  // Normalise to a single `user` variable regardless of which path is taken.
  const hookAuth = useAuth();
  const resolvedAuth = providedAuth || hookAuth;
  const user = resolvedAuth.user ?? resolvedAuth.currentUser;
  const isAuthenticated = resolvedAuth.isAuthenticated;

  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check if push notifications are supported
  useEffect(() => {
    const checkSupport = () => {
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        setIsSupported(true);
        setPermission(Notification.permission);
      } else {
        setIsSupported(false);
      }
    };

    checkSupport();
  }, []);

  // Register service worker
  const registerServiceWorker = useCallback(async () => {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service workers are not supported');
    }

    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', registration);
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      throw error;
    }
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      throw new Error('Push notifications are not supported');
    }

    try {
      const permission = await Notification.requestPermission();
      setPermission(permission);
      return permission;
    } catch (error) {
      console.error('Permission request failed:', error);
      throw error;
    }
  }, [isSupported]);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isAuthenticated || !user) {
      throw new Error('User must be authenticated');
    }

    setIsLoading(true);
    setError(null);

    try {
      // Register service worker
      const registration = await registerServiceWorker();

      // Request permission
      const permission = await requestPermission();
      if (permission !== 'granted') {
        throw new Error('Notification permission denied');
      }

      // Subscribe to push manager
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      // Send subscription to backend
      await api.post('/push/subscribe', {
        subscription: pushSubscription,
        userAgent: navigator.userAgent,
        deviceInfo: `${navigator.platform} - ${navigator.language}`
      });

      setSubscription(pushSubscription);
      setIsSubscribed(true);

      return pushSubscription;
    } catch (error) {
      console.error('Subscription failed:', error);
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user, registerServiceWorker, requestPermission]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      let currentSubscription = subscription;

      // If subscription state is null (e.g. after page reload), try to get it from SW
      if (!currentSubscription && 'serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        currentSubscription = await registration.pushManager.getSubscription();
      }

      if (!currentSubscription) {
        // No subscription found in browser, just reset UI state
        setIsSubscribed(false);
        setSubscription(null);
        return true;
      }

      // Unsubscribe from push manager
      const unsubscribed = await currentSubscription.unsubscribe();

      if (unsubscribed) {
        // Notify backend
        await api.post('/push/unsubscribe', {
          endpoint: currentSubscription.endpoint
        });

        setSubscription(null);
        setIsSubscribed(false);
      }

      return unsubscribed;
    } catch (error) {
      console.error('Unsubscription failed:', error);
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [subscription]);

  // Check current subscription status
  const checkSubscriptionStatus = useCallback(async () => {
    if (!isAuthenticated || !user) return;

    try {
      // 1. First, check if the current browser actually has a subscription
      let localSubscription = null;
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        localSubscription = await registration.pushManager.getSubscription();
      }

      if (!localSubscription) {
        setIsSubscribed(false);
        setSubscription(null);
        return; // Current device is definitely not subscribed
      }

      // 2. We have a local subscription. Check if it is still active on the backend
      const response = await api.get('/push/status');
      const { subscriptions } = response.data.data;

      if (subscriptions && subscriptions.length > 0) {
        // Find our specific device's subscription in the backend list
        const activeSub = subscriptions.find(
          sub => sub.endpoint === localSubscription.endpoint && sub.isActive
        );

        if (activeSub) {
          setIsSubscribed(true);
          setSubscription(localSubscription);
        } else {
          // Backend says it's inactive or deleted, but browser still has it.
          setIsSubscribed(false);
          setSubscription(null);
        }
      } else {
        setIsSubscribed(false);
        setSubscription(null);
      }
    } catch (error) {
      console.error('Failed to check subscription status:', error);
    }
  }, [isAuthenticated, user]);

  // Auto-check subscription status when user logs in
  useEffect(() => {
    if (isAuthenticated && user) {
      checkSubscriptionStatus();
    } else {
      setIsSubscribed(false);
      setSubscription(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);

  return {
    isSupported,
    permission,
    isSubscribed,
    subscription,
    isLoading,
    error,
    subscribe,
    unsubscribe,
    requestPermission,
    checkSubscriptionStatus,
    registerServiceWorker
  };
};
